'use client';

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { logger } from './logger';
import { isHelpSystemEnabled } from './feature-flags';

export type ChecklistItemId = 
  | 'welcome-tour'
  | 'first-scenario'
  | 'compare-scenarios'
  | 'configure-shock-parameters'
  | 'configure-instruments'
  | 'first-projection';

interface ChecklistItem {
  id: ChecklistItemId;
  label: string;
  description: string;
  completed: boolean;
  link?: string;
}

interface OnboardingState {
  items: ChecklistItem[];
  dismissed: boolean;
}

interface OnboardingContextValue {
  items: ChecklistItem[];
  dismissed: boolean;
  completeItem: (id: ChecklistItemId) => void;
  uncompleteItem: (id: ChecklistItemId) => void;
  dismissChecklist: () => void;
  resetChecklist: () => void;
  getProgress: () => { completed: number; total: number; percentage: number };
}

const OnboardingContext = createContext<OnboardingContextValue | undefined>(undefined);

const STORAGE_KEY = 'dt-debtcast-onboarding';

const defaultItems: ChecklistItem[] = [
  {
    id: 'welcome-tour',
    label: 'Take the welcome tour',
    description: 'Learn the basics: navigation, scenario analysis, and stochastic projections',
    completed: false,
    link: undefined,
  },
  {
    id: 'first-scenario',
    label: 'Complete your first scenario analysis',
    description: 'Select scenarios (Baseline, Revenue Shock, Expenditure Shock, Interest Rate Shock) and view key metrics: debt-to-GDP ratios, gross financing needs, GDP growth, and interest rates',
    completed: false,
    link: '/modules/scenario-analysis',
  },
  {
    id: 'compare-scenarios',
    label: 'Compare multiple scenarios side-by-side',
    description: 'Use the comparison view to analyze debt-to-GDP, gross financing needs, debt service, and debt-to-revenue ratios across different economic scenarios simultaneously',
    completed: false,
    link: '/modules/scenario-analysis',
  },
  {
    id: 'configure-shock-parameters',
    label: 'Configure shock parameters',
    description: 'Set magnitude, duration, and type (statistical vs manual) for revenue, expenditure, interest rate, and intergovernmental revenue shocks. These parameters control how economic disruptions affect debt sustainability calculations in shock scenarios',
    completed: false,
    link: '#global-settings',
  },
  {
    id: 'configure-instruments',
    label: 'Configure borrowing instruments',
    description: 'Define debt instruments (bonds/loans) with interest rates, maturities, and allocation shares. Instruments determine interest costs and debt service calculations through the debt engine\'s cohort-based repayment logic',
    completed: false,
    link: '#global-settings',
  },
  {
    id: 'first-projection',
    label: 'Generate your first stochastic projection',
    description: 'Run Monte Carlo simulations to analyze uncertainty in debt projections. Configure parameters (simulations count, projection years, target debt-to-GDP) and interpret the fan chart showing probability bands for future debt-to-GDP ratios',
    completed: false,
    link: '/modules/stochastic-projection',
  },
];

function loadOnboardingState(): OnboardingState {
  if (typeof window === 'undefined') {
    return {
      items: defaultItems,
      dismissed: false,
    };
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle new items
      const items = defaultItems.map(item => {
        const storedItem = parsed.items?.find((i: ChecklistItem) => i.id === item.id);
        return storedItem ? { ...item, completed: storedItem.completed } : item;
      });
      return {
        items,
        dismissed: parsed.dismissed || false,
      };
    }
  } catch (error) {
    logger.warn('Failed to load onboarding state from localStorage', { error });
  }

  return {
    items: defaultItems,
    dismissed: false,
  };
}

function saveOnboardingState(state: OnboardingState): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      items: state.items,
      dismissed: state.dismissed,
    }));
  } catch (error) {
    logger.warn('Failed to save onboarding state to localStorage', { error });
  }
}

export function OnboardingProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<OnboardingState>(loadOnboardingState);

  useEffect(() => {
    saveOnboardingState(state);
  }, [state]);

  const completeItem = useCallback((id: ChecklistItemId) => {
    setState(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.id === id ? { ...item, completed: true } : item
      ),
    }));

    logger.info('Onboarding item completed', { id });
  }, []);

  const uncompleteItem = useCallback((id: ChecklistItemId) => {
    setState(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.id === id ? { ...item, completed: false } : item
      ),
    }));

    logger.info('Onboarding item uncompleted', { id });
  }, []);

  const dismissChecklist = useCallback(() => {
    setState(prev => ({
      ...prev,
      dismissed: true,
    }));

    logger.info('Onboarding checklist dismissed');
  }, []);

  const resetChecklist = useCallback(() => {
    setState({
      items: defaultItems,
      dismissed: false,
    });

    logger.info('Onboarding checklist reset');
  }, []);

  const getProgress = useCallback(() => {
    const completed = state.items.filter(item => item.completed).length;
    const total = state.items.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, total, percentage };
  }, [state.items]);

  const value: OnboardingContextValue = {
    items: state.items,
    dismissed: state.dismissed,
    completeItem,
    uncompleteItem,
    dismissChecklist,
    resetChecklist,
    getProgress,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

// Default values when OnboardingProvider is not available (help system disabled)
const defaultOnboardingContextValue: OnboardingContextValue = {
  items: [],
  dismissed: false,
  completeItem: () => {},
  uncompleteItem: () => {},
  dismissChecklist: () => {},
  resetChecklist: () => {},
  getProgress: () => ({ completed: 0, total: 0, percentage: 0 }),
};

export function useOnboarding(): OnboardingContextValue {
  const context = useContext(OnboardingContext);
  
  // If help system is disabled, return default values instead of throwing
  if (context === undefined) {
    if (!isHelpSystemEnabled()) {
      return defaultOnboardingContextValue;
    }
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}

